import useSWR, { mutate } from 'swr';

import { agentSessionKey, agentSessionsKey, type AgentSession } from '@/api-models/agent-sessions';
import { fetchEndpointData } from '@/utils/fetch.client';

// ── Types ────────────────────────────────────────────────────────────

/**
 * One saved version of a session's plan. The table is append-only: the current plan is the
 * highest `version`, and a revert appends a copy of an older one rather than removing anything,
 * so reverting is itself undoable.
 */
export interface SessionPlan {
  id: string;
  session_id: string;
  version: number;
  content_md: string;
  /** `'agent'` when written by the planning agent, `'user'` when edited here. */
  created_by: 'agent' | 'user';
  created_at: string;
}

/** A version without its body, for the history dropdown. */
export interface SessionPlanVersion {
  id: string;
  version: number;
  created_by: 'agent' | 'user';
  created_at: string;
}

/** Mirrors `PlanResponse` (`routes/agent_sessions/plan.rs`). */
interface PlanResponse {
  /** `null` until the agent (or the user) has saved a first version. */
  plan: SessionPlan | null;
  versions: SessionPlanVersion[];
}

export const sessionPlanKey = (sessionId: string) => `${agentSessionKey(sessionId)}/plan`;

// ── SWR Hooks ──────────────────────────────────────────────────────────

/**
 * A session's current plan and its version history.
 *
 * Polls while the planning agent is working, since the plan document changes underneath the user
 * whenever the agent saves a revision — there is no push channel for it, only the thread's.
 */
export function useSessionPlan(sessionId: string | undefined, live = false) {
  const key = sessionId ? sessionPlanKey(sessionId) : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<PlanResponse>(key!), {
    refreshInterval: live ? 5000 : 0,
    revalidateOnFocus: true,
  });
  return {
    plan: data?.plan ?? null,
    versions: data?.versions ?? [],
    isLoading,
    error,
    mutate,
  };
}

/** Full version bodies, for previewing a version before reverting to it. */
export function useSessionPlanVersions(sessionId: string | undefined, enabled = true) {
  const key = sessionId && enabled ? `${sessionPlanKey(sessionId)}/versions` : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<SessionPlan[]>(key!));
  return { versions: data ?? [], isLoading, error, mutate };
}

// ── Action Functions ───────────────────────────────────────────────────

/** Save a user edit as a new version. */
export async function updateSessionPlan(sessionId: string, contentMd: string): Promise<SessionPlan> {
  const plan = await fetchEndpointData<SessionPlan>(sessionPlanKey(sessionId), {
    method: 'PUT',
    body: { content_md: contentMd },
  });
  void mutate(sessionPlanKey(sessionId));
  return plan;
}

/** Revert to an earlier version by appending a copy of it. */
export async function revertSessionPlan(sessionId: string, version: number): Promise<SessionPlan> {
  const plan = await fetchEndpointData<SessionPlan>(`${sessionPlanKey(sessionId)}/revert`, {
    method: 'POST',
    body: { version },
  });
  void mutate(sessionPlanKey(sessionId));
  return plan;
}

/**
 * Turn a finished plan into a coding session.
 *
 * This is the first point at which a sandbox is provisioned — everything before it cost nothing
 * but model tokens. The returned session is already `provisioning`, so writing it into the detail
 * cache lets the coding view paint the right state on its first frame.
 */
export async function startCodingSession(sessionId: string): Promise<AgentSession> {
  const session = await fetchEndpointData<AgentSession>(`${agentSessionKey(sessionId)}/start-coding`, {
    method: 'POST',
  });
  await mutate(agentSessionKey(sessionId), session, { revalidate: false });
  void mutate(agentSessionsKey(session.team_id));
  return session;
}
